import React, { useEffect, useState } from 'react';
import axios from '../../axiosConfig';
import { useNavigate } from 'react-router-dom';
import '../../styles/LandingPage.css';
import SessionExpiredModal from "./SessionExpiredModal";
import logoWhite from '../../assets/logo-white.png';
import { useSubscription } from '../../context/SubscriptionContext';

const LandingPage = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState({ email: '', password: '' });
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const navigate = useNavigate();

    // Get initializeAfterLogin from subscription context
    const { initializeAfterLogin } = useSubscription();

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        // Clear error when user starts typing
        if (error) {
            setError('');
        }
    };

    useEffect(() => {
        // Check if there's a session expired message
        const message = localStorage.getItem("sessionExpiredMessage");
        if (message) {
            setIsModalOpen(true);
            localStorage.removeItem("sessionExpiredMessage");
        }

        // Clear any stale auth data
        const token = localStorage.getItem('jwt');
        if (!token) {
            localStorage.removeItem('userType');
            sessionStorage.clear();
        }
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            const response = await axios.post('/api/v1/auth/login', formData);
            const { jwt, role, hasSchool } = response.data;

            // Store JWT token
            localStorage.setItem('jwt', jwt);

            // Determine and store user type based on role
            let userType = 'UNKNOWN';
            if (role === 'ROLE_TRAINER') {
                userType = 'TRAINER';
            } else if (role === 'ROLE_OWNER') {
                userType = 'OWNER';
            }

            localStorage.setItem('userType', userType);

            // Log for debugging
            console.log('Login successful:', { role, userType, hasSchool });

            // Initialize subscription check BEFORE navigating
            // This ensures the subscription modal will show if needed
            if (userType === 'TRAINER') {
                await initializeAfterLogin();
            }

            // Navigate based on role
            if (role === 'ROLE_TRAINER') {
                navigate('/dashboard');
            } else if (role === 'ROLE_OWNER') {
                navigate('/owner/dashboard');
            } else {
                // Fallback for unknown roles
                navigate('/dashboard');
            }
        } catch (err) {
            console.error('Login error:', err);

            // Handle different error types
            if (err.response?.status === 429) {
                setError('Zbyt wiele prób logowania. Proszę poczekać chwilę.');
            } else if (err.response?.status === 401) {
                setError('Nieprawidłowy email lub hasło. Proszę spróbować ponownie.');
            } else if (err.response?.status === 403) {
                setError('Konto zablokowane. Skontaktuj się z administratorem.');
            } else if (!err.response) {
                setError('Błąd połączenia. Sprawdź swoje połączenie internetowe.');
            } else {
                setError(err.response?.data?.error ||
                    err.response?.data?.message ||
                    'Wystąpił błąd podczas logowania. Spróbuj ponownie.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !isLoading) {
            handleSubmit(e);
        }
    };

    const handleForgotPassword = () => {
        // Save email if entered to pre-fill on forgot password page
        if (formData.email) {
            sessionStorage.setItem('forgotPasswordEmail', formData.email);
        }
        navigate('/forgot-password');
    };

    return (
        <div className="landing-page-container">
            {isModalOpen && <SessionExpiredModal onClose={() => setIsModalOpen(false)} />}
            <div className="landing-page-left-panel">
                <div className="landing-page-logo-container">
                    <img
                        src={logoWhite}
                        alt="Petboard"
                        className="landing-page-logo-image"
                    />
                    <p className="landing-page-app-description">Wspólna droga do lepszego treningu</p>
                </div>
            </div>
            <div className="landing-page-right-panel">
                <div className="landing-page-mobile-logo-wrapper">
                    <img
                        src={logoWhite}
                        alt="Petboard"
                        className="landing-page-logo-image"
                    />
                </div>
                <form className="landing-page-login-form" onSubmit={handleSubmit}>
                    <div className="landing-page-form-group">
                        <input
                            id="email"
                            type="email"
                            name="email"
                            placeholder="E-mail"
                            autoComplete="email"
                            className="landing-page-input"
                            value={formData.email}
                            onChange={handleChange}
                            onKeyPress={handleKeyPress}
                            disabled={isLoading}
                            required
                        />
                    </div>
                    <div className="landing-page-form-group landing-page-password-group">
                        <div className="landing-page-password-label-container">
                            <label htmlFor="password" className="landing-page-form-label"></label>
                        </div>
                        <input
                            id="password"
                            type="password"
                            name="password"
                            autoComplete="current-password"
                            placeholder="Hasło"
                            className="landing-page-input"
                            value={formData.password}
                            onChange={handleChange}
                            onKeyPress={handleKeyPress}
                            disabled={isLoading}
                            required
                        />
                    </div>

                    {error && (
                        <div className="landing-page-error-container">
                            <p className="landing-page-error">{error}</p>
                        </div>
                    )}

                    <button
                        type="submit"
                        className="landing-page-login-btn"
                        disabled={isLoading}
                    >
                        {isLoading ? 'Logowanie...' : 'Zaloguj się'}
                    </button>

                    <p
                        className="landing-page-forgot-password landing-page-bottom-link"
                        onClick={handleForgotPassword}
                    >
                        Zapomniałeś hasła?
                    </p>

                    <div className="landing-page-register-section">
                        <p className="landing-page-register-prompt">Nie masz jeszcze konta?</p>
                        <button
                            type="button"
                            className="landing-page-register-btn"
                            onClick={() => navigate('/register-trainer')}
                            disabled={isLoading}
                        >
                            Zarejestruj swoją szkołę
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default LandingPage;